/**
 * Bulk import only understands CSV: the presigned upload key always ends in `-lead.csv` /
 * `-customer.csv` and the backend parses whatever lands there with Go's `encoding/csv`. An
 * Excel file is therefore converted to CSV in the browser before it is uploaded.
 *
 * The output is shaped for that parser:
 * - every row has exactly as many fields as the header (it rejects ragged rows);
 * - blank columns to the right of the header are dropped (Excel often saves them, and the
 *   backend then fails with "duplicate header found: ");
 * - no BOM, which would otherwise become part of the first header name.
 */

const EXCEL_EXTENSIONS = ['.xls', '.xlsx'];

export const SPREADSHEET_ERROR = {
  UNREADABLE: 'unreadable',
  EMPTY: 'empty',
  BLANK_HEADER: 'blank_header',
  DUPLICATE_HEADER: 'duplicate_header',
};

const spreadsheetError = (code) => {
  const error = new Error(`Spreadsheet ${code}`);
  error.code = code;
  return error;
};

export const isExcelFile = (file) => {
  const name = String((file && file.name) || '').toLowerCase();
  return EXCEL_EXTENSIONS.some((ext) => name.endsWith(ext));
};

const readAsArrayBuffer = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = () => reject(reader.error);
  reader.readAsArrayBuffer(file);
});

const isGeneralFormat = (format) => !format || format === 'General';

// A date format carries a day or year token once quoted text, escapes and [..] sections are
// removed; SSF.is_date alone also matches time-only formats such as "h:mm".
const hasDatePart = (format) => /[dy]/i.test(String(format)
  .replace(/"[^"]*"/g, '')
  .replace(/\\./g, '')
  .replace(/\[[^\]]*\]/g, ''));

/**
 * The text a cell should contribute to the CSV.
 * - A whole number in a General-formatted cell is written in full, because its display text
 *   (`w`) shortens long values such as NRIC numbers to "9.81219E+11".
 * - A date is written as YYYY-MM-DD, the format the import templates ask for, rather than its
 *   display text: Excel's default short date displays as "2/14/85".
 * - Every other cell keeps its display text, so formats like leading-zero codes come out as
 *   they look in Excel.
 */
export const cellToText = (cell, SSF, { date1904 = false } = {}) => {
  if (!cell || cell.v === undefined || cell.v === null) return '';
  if (cell.t === 'n' && isGeneralFormat(cell.z) && Number.isSafeInteger(cell.v)) {
    return String(cell.v);
  }
  if (cell.t === 'n' && SSF && cell.z && SSF.is_date(cell.z) && hasDatePart(cell.z)) {
    return SSF.format('yyyy-mm-dd', cell.v, { date1904 });
  }
  if (cell.w !== undefined) return String(cell.w);
  return String(cell.v);
};

const escapeCsvField = (value) => (/[",\r\n]/.test(value)
  ? `"${value.replace(/"/g, '""')}"`
  : value);

export const rowsToCsv = (rows) => rows
  .map((row) => row.map(escapeCsvField).join(','))
  .join('\n');

const FIELD_KEY = /^[a-z][a-z0-9_]*$/;
// The templates have two rows above the field keys; a few more allow for a user's own title
// rows. Looking further down would let a data row of lowercase values pass for the header.
const HEADER_SEARCH_ROWS = 5;

/**
 * The sample XLS templates put a row of section titles and a row of type hints ("NUMBER",
 * "DATE (YYYY-MM-DD)") above the field keys, while the CSV samples start with the keys. The
 * header is the first of the top rows whose filled cells are all field keys and include
 * `name`, which every lead and customer template has; without one, the first row is the header
 * as in a CSV. Requiring `name` keeps a lowercase data row such as "alice,female" under a
 * "Name,Gender" header from being taken for the header.
 */
export const findHeaderIndex = (grid) => {
  const index = grid.slice(0, HEADER_SEARCH_ROWS).findIndex((row) => {
    const filled = row.map((value) => value.trim()).filter(Boolean);
    return filled.includes('name') && filled.every((value) => FIELD_KEY.test(value));
  });
  return Math.max(index, 0);
};

/**
 * Turns a grid of cell texts into the rows written to the CSV, header first, or throws a
 * SPREADSHEET_ERROR code.
 */
export const normalizeRows = (sheetGrid) => {
  const grid = sheetGrid.slice(findHeaderIndex(sheetGrid));
  const header = (grid[0] || []).map((name) => name.trim());
  let width = header.length;
  while (width > 0 && header[width - 1] === '') width -= 1;
  if (width === 0) throw spreadsheetError(SPREADSHEET_ERROR.EMPTY);

  const columns = header.slice(0, width);
  if (columns.includes('')) throw spreadsheetError(SPREADSHEET_ERROR.BLANK_HEADER);
  if (new Set(columns).size !== columns.length) {
    throw spreadsheetError(SPREADSHEET_ERROR.DUPLICATE_HEADER);
  }

  // Values right of the last header have no column to go to; dropping them would lose data
  // without a word, so they get the same error as a blank header between columns.
  const rows = grid.slice(1);
  if (rows.some((row) => row.slice(width).some((value) => value.trim() !== ''))) {
    throw spreadsheetError(SPREADSHEET_ERROR.BLANK_HEADER);
  }

  const body = rows
    .map((row) => Array.from({ length: width }, (_, i) => row[i] || ''))
    .filter((row) => row.some((value) => value.trim() !== ''));
  if (body.length === 0) throw spreadsheetError(SPREADSHEET_ERROR.EMPTY);

  return [columns, ...body];
};

/**
 * Builds rows from the cells the sheet actually holds, never from its bounds. The declared
 * `!ref` (Excel saves one down to row 1,048,576 when whole columns are formatted) or a single
 * stray cell far below the data would otherwise mean millions of empty cells to walk. Rows
 * without cells are left out, since normalizeRows drops blank rows anyway, and each row only
 * reaches its own last cell; normalizeRows pads rows to the header's width.
 */
export const sheetToGrid = (XLSX, sheet, options) => {
  const addresses = Object.keys(sheet || {}).filter((key) => key[0] !== '!');
  if (addresses.length === 0) return [];
  const rows = new Map();
  let firstCol = Infinity;
  addresses.forEach((address) => {
    const { r, c } = XLSX.utils.decode_cell(address);
    firstCol = Math.min(firstCol, c);
    if (!rows.has(r)) rows.set(r, []);
    rows.get(r).push({ c, address });
  });
  return [...rows.keys()].sort((a, b) => a - b).map((r) => {
    const cells = rows.get(r);
    const row = new Array(Math.max(...cells.map(({ c }) => c)) - firstCol + 1).fill('');
    cells.forEach(({ c, address }) => {
      row[c - firstCol] = cellToText(sheet[address], XLSX.SSF, options);
    });
    return row;
  });
};

/**
 * Converts the first sheet of an .xls / .xlsx File into a CSV File named after it.
 * Rejects with an Error whose `code` is one of SPREADSHEET_ERROR.
 */
export const excelToCsvFile = async (file) => {
  const XLSX = await import('xlsx');
  let workbook;
  try {
    workbook = XLSX.read(await readAsArrayBuffer(file), { type: 'array', cellNF: true });
  } catch (e) {
    throw spreadsheetError(SPREADSHEET_ERROR.UNREADABLE);
  }

  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  // Workbooks from older Mac Excel count dates from 1904 instead of 1900.
  const date1904 = Boolean(workbook.Workbook && workbook.Workbook.WBProps
    && workbook.Workbook.WBProps.date1904);
  const csv = rowsToCsv(normalizeRows(sheetToGrid(XLSX, sheet, { date1904 })));
  const baseName = file.name.replace(/\.[^.]+$/, '');
  return new File([csv], `${baseName}.csv`, { type: 'text/csv' });
};
